// Server→client message reducer (Phase 5 Step 15a).
//
// Mechanical extraction of the switch previously inlined in main.ts.
// main.ts was running against the §10 400-line cap and Step 15b adds
// combat-envelope arms that would trip it; hoisting the reducer here
// keeps the orchestration file narrow while preserving every existing
// behavior byte-for-byte.
//
// Structural dep shape (not concrete classes) so unit tests can assemble
// minimal stubs without pulling PixiJS into the server test graph —
// same pattern as resume-handshake.ts.

import { applyMarketServerMessage, type MarketClientDeps } from "./market-client-handlers.ts";
import { applySkillTierReached, applyXpGranted } from "./skill-client-handlers.ts";
import { applyBootstrap, type BootstrapDeps, type BootstrapPayload } from "./resume-handshake.ts";
import { clearResumeState, saveResumeState } from "./resume-storage.ts";
import type { StationId } from "../../shared/stations.ts";
import { applyTravelStarted, applyTravelCleared, type TravelClientState } from "./travel-state.ts";
export type { TravelClientState } from "./travel-state.ts";
import { clientDebug } from "../../shared/debug.ts";
import {
  balance,
  type InventorySlot,
  type PlayerState,
  type ResourceNode,
  type ServerMessage,
  type SkillEntry,
  type WorkshopState,
} from "./types.ts";
import type { ClientMarketState } from "./market-ui.ts";
import {
  applyAbilityMissed,
  applyAbilityResolved,
  applyDamageDealt,
  applyDamageTaken,
  applyExpeditionPositions,
  applyLootRolled,
  applyMobDefeated,
  applyMobSpawned,
  applyPlayerDied,
  type CombatClientState,
} from "./combat-state.ts";
import {
  applyPartyDissolved,
  applyPartyInvite,
  applyPartyMemberJoined,
  applyPartyMemberLeft,
  type PartyClientState,
} from "./party-state.ts";
import type { EquippedItemReadout } from "../../shared/equip-messages.ts";
import {
  applyExpeditionCompleted,
  applyExpeditionReady,
  clearForExpedition,
  consumeAcceptedIfMatch,
  type ExpeditionClientState,
} from "./expedition-state.ts";

// Minimal structural surfaces — only the methods the reducer actually
// calls — to keep this module decoupled from concrete renderer / UI
// class bodies.
type RendererDep = {
  addPlayer(player: PlayerState): void;
  removePlayer(playerId: string): void;
  hasPlayer(playerId: string): boolean;
  setDisplayName(playerId: string, displayName: string): void;
};
type InterpDep = {
  seed(playerId: string, x: number, y: number, now: number): void;
  drop(playerId: string): void;
  target(playerId: string, x: number, y: number, now: number): void;
};
type InventoryUIDep = { update(inventory: InventorySlot[]): void };
type WorkbenchUIDep = {
  readonly isOpen: boolean;
  update(workshop: WorkshopState): void;
  refreshOnInventoryChange(): void;
};
type NodeRendererDep = {
  setDepleted(nodeId: string): void;
  setRespawned(nodeId: string): void;
};
type GatherUIDep = {
  start(durationMs: number): void;
  stop(): void;
};
type MobRendererDep = {
  spawn(mobId: string, x: number, y: number): void;
  defeat(mobId: string): void;
};

// Reducer's view of clientState. Superset of the shared fields the
// bootstrap dep uses, plus the zone/gather fields that the per-message
// handlers touch. Market state is tracked under its own handler module.
export type ReducerClientState = {
  self: PlayerState | null;
  players: Map<string, PlayerState>;
  inventory: InventorySlot[];
  activeGather: { nodeId: string; startedAt: number; expectedCompleteAt: number } | null;
  nodes: Map<string, ResourceNode>;
  currentZoneId: string | null;
  workshop: WorkshopState;
  skills: SkillEntry[];
  credits: number;
  market: ClientMarketState;
  combat: CombatClientState;
  party: PartyClientState;
  expedition: ExpeditionClientState;
  travel: TravelClientState;
  equippedItem: EquippedItemReadout | null; // Phase 7 Block C — hydrated from snapshot.equippedItem
};

export type ServerMessageReducerDeps = {
  readonly localPlayerId: string;
  readonly clientState: ReducerClientState;
  readonly renderer: RendererDep;
  readonly interp: InterpDep;
  readonly inventoryUI: InventoryUIDep;
  readonly workbenchUI: WorkbenchUIDep;
  readonly nodeRenderer: NodeRendererDep;
  readonly gatherUI: GatherUIDep;
  readonly mobRenderer: MobRendererDep;
  readonly marketDeps: MarketClientDeps;
  readonly localStorage: Storage;
  readonly bootstrapDeps: (now: number) => BootstrapDeps;
  readonly onExpeditionAccepted: () => void; // expeditionEntered for accepted id → WS reconnect to ExpeditionDO
  readonly onJumpCompleted: (stationId: StationId) => void; // jumpCompleted → reconnect to new station ZoneDO
  readonly onSkillTierReached: (skillId: string, tier: string) => void; // Phase 6 Block B
  readonly eventLog: { push(text: string, kind: string): void }; // Phase 7 Block E — DOM-free structural type
  // Phase 7 Block F — proximity invite + friends list updates.
  readonly onNearbyPlayers?: (players: import("../../shared/friends-messages.ts").NearbyPlayer[]) => void;
  readonly onFriendsListUpdated?: (friends: import("../../shared/friends-messages.ts").FriendEntry[]) => void;
};
// Exhaustive switch. `replay` recurses internally; main.ts advances per-envelope `seq` watermarks.
export function applyServerMessage(
  msg: ServerMessage,
  deps: ServerMessageReducerDeps,
): void {
  const now = performance.now();
  switch (msg.type) {
    case "snapshot": {
      // Persist the fresh watermark before applying payload so a
      // mid-apply crash leaves storage self-consistent (worst case:
      // next connect re-snapshots).
      saveResumeState(deps.localStorage, {
        epochId: msg.payload.epochId,
        lastSeenSeq: msg.payload.lastSeq,
      });
      applyBootstrap(msg.payload as BootstrapPayload, deps.bootstrapDeps(now));
      return;
    }
    case "replay":
      for (const envelope of msg.payload.envelopes) applyServerMessage(envelope, deps);
      return;
    case "playerJoined": {
      const { player } = msg.payload;
      if (player.playerId === deps.localPlayerId) return;
      deps.renderer.addPlayer(player);
      deps.clientState.players.set(player.playerId, { ...player });
      deps.interp.seed(player.playerId, player.x, player.y, now);
      return;
    }
    case "playerLeft": {
      const { playerId: leftId } = msg.payload;
      deps.renderer.removePlayer(leftId);
      deps.clientState.players.delete(leftId);
      deps.interp.drop(leftId);
      return;
    }
    case "playerMoved": {
      const { playerId: movedId, x, y } = msg.payload;
      if (movedId === deps.localPlayerId) return;
      const entry = deps.clientState.players.get(movedId);
      if (entry) { entry.x = x; entry.y = y; }
      if (!deps.renderer.hasPlayer(movedId)) {
        // A move for somebody we don't know about yet — create them
        // with a neutral color and seed interpolation. Proper re-sync
        // handshake is the resume path.
        const placeholder: PlayerState = { playerId: movedId, x, y, color: "#888888", displayName: "" };
        deps.renderer.addPlayer(placeholder);
        deps.clientState.players.set(movedId, { ...placeholder });
        deps.interp.seed(movedId, x, y, now);
        return;
      }
      deps.interp.target(movedId, x, y, now);
      return;
    }
    case "pong":
      return;
    case "error":
      applyMarketServerMessage(msg, deps.marketDeps);
      return;
    case "gatherStarted": {
      const { nodeId, durationMs, expectedCompleteAt } = msg.payload;
      deps.clientState.activeGather = { nodeId, startedAt: Date.now(), expectedCompleteAt };
      deps.gatherUI.start(durationMs);
      return;
    }
    case "gatherCompleted": {
      deps.clientState.activeGather = null;
      deps.clientState.inventory = msg.payload.inventory;
      deps.inventoryUI.update(msg.payload.inventory);
      deps.gatherUI.stop();
      deps.eventLog.push(`Gathered ${msg.payload.itemsGained.count}x ${msg.payload.itemsGained.itemId}`, "gather");
      return;
    }
    case "gatherCancelled": {
      deps.clientState.activeGather = null;
      deps.gatherUI.stop();
      clientDebug("gather cancelled:", msg.payload.reason);
      return;
    }
    case "nodeDepleted": {
      const { nodeId, respawnAt } = msg.payload;
      const depletedNode = deps.clientState.nodes.get(nodeId);
      if (depletedNode) { depletedNode.respawnAt = respawnAt; depletedNode.remainingYield = 0; }
      deps.nodeRenderer.setDepleted(nodeId);
      return;
    }
    case "nodeRespawned": {
      const respawnedNode = deps.clientState.nodes.get(msg.payload.nodeId);
      if (respawnedNode) {
        respawnedNode.respawnAt = null;
        // assumes server restored to balance.gathering.nodeYield.
        respawnedNode.remainingYield = balance.gathering.nodeYield;
      }
      deps.nodeRenderer.setRespawned(msg.payload.nodeId);
      return;
    }
    case "inventoryUpdated": {
      deps.clientState.inventory = msg.payload.inventory;
      deps.inventoryUI.update(msg.payload.inventory);
      // Market panel reflects inventory state; refresh so a now-depleted
      // selected slot renders correctly.
      if (deps.marketDeps.marketUI.isOpen) deps.marketDeps.marketUI.refresh();
      // Phase 7 Block D — workbench gates the Queue Craft button on
      // inventory; refresh so the materials column + queue button
      // reflect the post-mutation totals.
      if (deps.workbenchUI.isOpen) deps.workbenchUI.refreshOnInventoryChange();
      return;
    }
    case "craftQueued": case "craftCancelled": case "jobStarted": case "workshopUpdated":
      // These four workshop messages carry the same fresh WorkshopState snapshot.
      deps.clientState.workshop = msg.payload.workshop;
      if (deps.workbenchUI.isOpen) deps.workbenchUI.update(msg.payload.workshop);
      return;
    case "jobCompleted":
      // Phase 5 Step 19 — jobCompleted is the sole real-time notification of a finished job.
      deps.clientState.workshop = msg.payload.workshop;
      if (deps.workbenchUI.isOpen) deps.workbenchUI.update(msg.payload.workshop);
      deps.eventLog.push("Craft job ready to claim", "craft");
      return;
    case "listingCreated":
      applyMarketServerMessage(msg, deps.marketDeps);
      deps.eventLog.push(`Listed: ${msg.payload.listing.itemId} for ${msg.payload.listing.price}c`, "market");
      return;
    case "marketView":
    case "listingCancelled":
    case "creditsUpdated":
    case "priceHistory":
    case "remoteMarketView":
      applyMarketServerMessage(msg, deps.marketDeps);
      return;
    case "listingSold": {
      // Phase 7 Block A finding 9.5 — buyer also receives this push (prunes ghost rows).
      applyMarketServerMessage(msg, deps.marketDeps);
      const { itemId, price, seller } = msg.payload;
      if (deps.localPlayerId === seller) {
        deps.eventLog.push(`Sold: ${itemId} for ${price}c`, "market");
      }
      return;
    }
    // Combat envelopes — Phase 5 Step 15b. Pure state updates via
    // combat-state.ts reducers; rendering is driven off clientState
    // by combat-ui.ts each frame.
    case "abilityResolved":
      applyAbilityResolved(deps.clientState.combat, msg.payload, deps.localPlayerId, now);
      return;
    case "abilityMissed":
      applyAbilityMissed(deps.clientState.combat, msg.payload, now);
      return;
    case "damageDealt":
      applyDamageDealt(deps.clientState.combat, msg.payload);
      return;
    case "damageTaken":
      applyDamageTaken(deps.clientState.combat, msg.payload, deps.localPlayerId);
      return;
    case "playerDied":
      applyPlayerDied(deps.clientState.combat, msg.payload, deps.localPlayerId);
      return;
    case "mobSpawned":
      // Step 17d: payload.mobType feeds the bossMobId latch for the
      // top-center boss HP bar.
      applyMobSpawned(deps.clientState.combat, msg.payload);
      deps.mobRenderer.spawn(msg.payload.mobId, msg.payload.position.x, msg.payload.position.y);
      return;
    case "mobDefeated":
      applyMobDefeated(deps.clientState.combat, msg.payload);
      deps.mobRenderer.defeat(msg.payload.mobId);
      return;
    case "lootRolled":
      applyLootRolled(deps.clientState.combat, msg.payload, deps.localPlayerId, now);
      return;
    case "expeditionPositions":
      applyExpeditionPositions(deps.clientState.combat, msg.payload);
      return;
    case "itemRepaired": { // Phase 5 Step 18 — durable repair confirmation; event-log + in-place inventory update.
      const { inventoryRowid, durabilityAfter } = msg.payload;
      const row = deps.clientState.inventory.find((s) => s.rowid === inventoryRowid);
      if (row !== undefined) row.durabilityCurrent = durabilityAfter;
      deps.inventoryUI.update(deps.clientState.inventory);
      deps.marketDeps.marketUI.clearRepairPending(inventoryRowid);
      if (deps.marketDeps.marketUI.isOpen) deps.marketDeps.marketUI.refresh();
      deps.eventLog.push("Repaired!", "repair");
      return;
    }
    // Party envelopes — Phase 5 Step 16b. Pure state updates via
    // party-state.ts; party-ui renders off clientState.party each frame.
    case "partyInvite":
      applyPartyInvite(deps.clientState.party, msg.payload, now);
      deps.eventLog.push(`Party invite from ${msg.payload.leaderName}`, "party");
      return;
    case "partyMemberJoined":
      applyPartyMemberJoined(deps.clientState.party, msg.payload, deps.localPlayerId);
      deps.eventLog.push("Party member joined", "party");
      return;
    case "partyMemberLeft":
      applyPartyMemberLeft(deps.clientState.party, msg.payload, deps.localPlayerId);
      deps.eventLog.push("Party member left", "party");
      return;
    case "partyDissolved":
      applyPartyDissolved(deps.clientState.party, msg.payload);
      deps.eventLog.push("Party dissolved", "party");
      return;
    case "expeditionReady": // Phase 5 Step 17 — expedition entry handshake wire-up
      applyExpeditionReady(deps.clientState.expedition, msg.payload, now);
      return;
    case "expeditionEntered": {
      // Resume-replay path for the durable expeditionEntered. Clear resume
      // token (cross-DO transition; ExpeditionDO needs a fresh snapshot).
      if (consumeAcceptedIfMatch(deps.clientState.expedition, msg.payload.expeditionId)) {
        clearResumeState(deps.localStorage);
        deps.onExpeditionAccepted();
      }
      deps.eventLog.push("Expedition entered", "expedition");
      return;
    }
    case "expeditionReadyConfirmed": {
      // In-band "go reconnect" cue; same latch semantics as expeditionEntered.
      if (consumeAcceptedIfMatch(deps.clientState.expedition, msg.payload.expeditionId)) {
        // Cross-DO transition — see expeditionEntered arm for rationale.
        clearResumeState(deps.localStorage);
        deps.onExpeditionAccepted();
      }
      return;
    }
    case "expeditionExited":
      clearForExpedition(deps.clientState.expedition, msg.payload.expeditionId);
      clearResumeState(deps.localStorage); // exit-side pairing: player returned to ZoneDO
      return;
    case "expeditionCompleted":
      clientDebug("expeditionCompleted", msg.payload);
      applyExpeditionCompleted(deps.clientState.expedition, msg.payload);
      clearResumeState(deps.localStorage); // expedition over; next ZoneDO connect gets fresh snapshot
      deps.eventLog.push(`Expedition complete (${msg.payload.reason})`, "expedition");
      return;
    // Phase 6 Block A — jump-gate travel envelopes.
    case "jumpStarted":
    case "traveling":
      applyTravelStarted(deps.clientState.travel, msg.payload.target, msg.payload.completesAt);
      return;
    case "jumpCompleted":
      applyTravelCleared(deps.clientState.travel);
      clearResumeState(deps.localStorage);
      deps.onJumpCompleted(msg.payload.stationId);
      return;
    case "skillTierReached":
      applySkillTierReached(msg.payload, deps.clientState.skills, deps.onSkillTierReached, deps.eventLog);
      return;
    case "xpGranted": // Phase 7 Block E — XP feedback in event log.
      applyXpGranted(msg.payload, deps.clientState.skills, deps.eventLog);
      return;
    case "displayNameSet": { // Phase 7 Block B — propagate to self + zone roster + nameplate
      const { playerId: targetId, displayName } = msg.payload;
      if (deps.clientState.self?.playerId === targetId) deps.clientState.self.displayName = displayName;
      const peer = deps.clientState.players.get(targetId);
      if (peer !== undefined) peer.displayName = displayName;
      deps.renderer.setDisplayName(targetId, displayName);
      return;
    }
    case "equippedItemUpdated": // Phase 7 Block C — refresh equipped state + hotbar E badge.
      deps.clientState.equippedItem = msg.payload.equipped;
      deps.inventoryUI.update(deps.clientState.inventory);
      return;
    case "nearbyPlayers": deps.onNearbyPlayers?.(msg.payload.players); return; // Phase 7 Block F
    case "friendsListUpdated": deps.onFriendsListUpdated?.(msg.payload.friends); return; // Phase 7 Block F
    default: { const _exhaustive: never = msg; void _exhaustive; }
  }
}
